import React, { useEffect, useMemo, useState } from 'react';
import DashboardLayout from '../components/DashboardLayout';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from '../components/ui/dropdown-menu';
import { Label } from '../components/ui/label';
import {
  Package, Truck, Calendar, Box, CheckCircle2, AlertTriangle, RotateCcw,
  Search, Loader2, ExternalLink, RefreshCw, MapPin, KeyRound, ShieldCheck,
  X, MoreHorizontal, FilePlus, CalendarClock,
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';

/**
 * Admin Shipping Pipeline View
 * ----------------------------
 * Reuses the same /api/shipping/vendor/pipeline-summary endpoint as
 * Pack & Ship — that endpoint already scopes to ALL vendors when the
 * caller is admin / super_admin. We render the 7 active shipment
 * buckets in tabs so ops can audit every parcel mid-flight, whether
 * the seller is using Aniyor-managed Delhivery shipping OR the new
 * self-managed verification flow (iter-229d).
 *
 * Each row carries:
 *   - vendor name (so admin knows whose parcel this is)
 *   - canonical_state badge
 *   - shipping_method chip (Aniyor / Self-managed)
 *   - verification channel (`delivered_via`) for completed self-managed
 *   - dispute / OTP-locked flags so admin can deep-link to triage
 */

const BUCKETS = [
  // iter-229f — Pending is admin-only (vendor's Pack & Ship has it removed).
  // Lets admin help vendors stuck on AWB generation.
  { id: 'pending', label: 'Pending', icon: Package, color: 'text-gray-700 bg-gray-200' },
  { id: 'manifest', label: 'Manifest', icon: Calendar, color: 'text-indigo-700 bg-indigo-100' },
  { id: 'pickup', label: 'Pickup', icon: Truck, color: 'text-cyan-700 bg-cyan-100' },
  { id: 'shipped', label: 'Shipped', icon: Box, color: 'text-violet-700 bg-violet-100' },
  { id: 'in_transit', label: 'In-Transit', icon: Truck, color: 'text-yellow-700 bg-yellow-100' },
  { id: 'delivered', label: 'Delivered', icon: CheckCircle2, color: 'text-green-700 bg-green-100' },
  { id: 'ndr', label: 'NDR', icon: AlertTriangle, color: 'text-orange-700 bg-orange-100' },
  { id: 'rto', label: 'RTO', icon: RotateCcw, color: 'text-rose-700 bg-rose-100' },
];

const STATE_LABELS = {
  packed: 'Packed',
  manifest: 'Manifested',
  picked_up: 'Picked up',
  in_transit: 'In transit',
  delivered: 'Delivered',
  ndr: 'NDR',
  rto_initiated: 'RTO',
  cancelled: 'Cancelled',
};

const Shipping = () => {
  const navigate = useNavigate();
  const { apiCall } = useAuth();

  const [activeTab, setActiveTab] = useState('manifest');
  const [pipeline, setPipeline] = useState({ counts: {}, totals: {} });
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [search, setSearch] = useState('');
  const [methodFilter, setMethodFilter] = useState('all');
  const [vendorFilter, setVendorFilter] = useState('all');

  // iter-229e — Bulk-action selection state. Keys are shipment.id;
  // selections are scoped per-tab so switching tabs doesn't carry stale
  // checkboxes forward (which would lead to admin-overriding the wrong
  // shipments).
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkRunning, setBulkRunning] = useState(false);
  const [overrideOpen, setOverrideOpen] = useState(false);
  const [overrideReason, setOverrideReason] = useState('');
  // iter-229f — Bulk schedule pickup dialog state
  const [pickupOpen, setPickupOpen] = useState(false);
  const [pickupDate, setPickupDate] = useState(() => {
    const d = new Date(); d.setDate(d.getDate() + 1);
    return d.toISOString().slice(0, 10);
  });
  const [pickupTime, setPickupTime] = useState('14:00');
  const [pickupPhoneOverride, setPickupPhoneOverride] = useState('');

  // Reset selections whenever the visible tab changes.
  useEffect(() => {
    setSelectedIds(new Set());
  }, [activeTab]);

  const toggleSelect = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const selectAll = (rows) => {
    setSelectedIds((prev) => {
      const allIds = rows.map((r) => r.id).filter(Boolean);
      const allSelected = allIds.every((id) => prev.has(id));
      if (allSelected) return new Set(); // toggle off
      return new Set(allIds);
    });
  };

  const runBulkNdrAction = async () => {
    const ids = Array.from(selectedIds);
    if (ids.length === 0) return;
    setBulkRunning(true);
    try {
      const res = await apiCall('/api/admin/shipments/bulk-ndr-action', {
        method: 'POST',
        body: JSON.stringify({ shipment_ids: ids, action: 'RE-ATTEMPT' }),
      });
      toast.success(
        `Re-attempt submitted to Delhivery — ${res.succeeded}/${res.total} succeeded${res.failed ? `, ${res.failed} failed` : ''}.`
      );
      setSelectedIds(new Set());
      loadPipeline({ background: true });
    } catch (err) {
      toast.error(err?.message || 'Bulk NDR action failed');
    } finally {
      setBulkRunning(false);
    }
  };

  const runBulkOverride = async () => {
    const ids = Array.from(selectedIds);
    const reason = overrideReason.trim();
    if (ids.length === 0 || reason.length < 20) {
      toast.error('Reason must be at least 20 characters.');
      return;
    }
    setBulkRunning(true);
    try {
      const res = await apiCall('/api/admin/shipments/bulk-manual-deliver', {
        method: 'POST',
        body: JSON.stringify({ shipment_ids: ids, reason }),
      });
      toast.success(
        `Admin override applied — ${res.succeeded}/${res.total} delivered${res.failed ? `, ${res.failed} failed` : ''}.`
      );
      setSelectedIds(new Set());
      setOverrideOpen(false);
      setOverrideReason('');
      loadPipeline({ background: true });
    } catch (err) {
      toast.error(err?.message || 'Bulk override failed');
    } finally {
      setBulkRunning(false);
    }
  };

  const runBulkSchedulePickup = async () => {
    const ids = Array.from(selectedIds);
    if (ids.length === 0) return;
    setBulkRunning(true);
    try {
      const res = await apiCall('/api/admin/shipments/bulk-schedule-pickup', {
        method: 'POST',
        body: JSON.stringify({
          shipment_ids: ids,
          pickup_date: pickupDate,
          pickup_time: pickupTime,
          rider_phone_override: pickupPhoneOverride.trim() || null,
        }),
      });
      const skippedCount = (res.skipped || []).length;
      toast.success(
        `Pickup scheduled — ${res.shipments_scheduled} shipment${res.shipments_scheduled === 1 ? '' : 's'} ` +
        `across ${res.vendors_succeeded}/${res.vendors_touched} vendor${res.vendors_touched === 1 ? '' : 's'}` +
        (skippedCount ? `, ${skippedCount} skipped.` : '.')
      );
      // Surface per-vendor failures in a follow-up toast so admin sees details
      const failures = (res.vendor_results || []).filter((v) => !v.ok);
      if (failures.length) {
        toast.error(
          `${failures.length} vendor pickup${failures.length === 1 ? '' : 's'} failed: ` +
          failures.map((f) => f.error || 'unknown').slice(0, 2).join(' / ')
        );
      }
      setSelectedIds(new Set());
      setPickupOpen(false);
      setPickupPhoneOverride('');
      loadPipeline({ background: true });
    } catch (err) {
      toast.error(err?.message || 'Bulk pickup schedule failed');
    } finally {
      setBulkRunning(false);
    }
  };

  // iter-229f — Per-row single action: mark this one shipment delivered.
  // Used from the "..." dropdown when admin wants to override just one
  // row without going through the bulk flow.
  const singleMarkDelivered = async (sh) => {
    const reason = window.prompt(
      `Mark shipment ${sh.awb || sh.id} delivered? Enter a reason (≥20 chars):`
    );
    if (!reason || reason.trim().length < 20) {
      if (reason !== null) toast.error('Reason must be at least 20 characters.');
      return;
    }
    try {
      await apiCall(`/api/admin/shipments/${sh.id}/manual-deliver`, {
        method: 'POST',
        body: JSON.stringify({ reason: reason.trim() }),
      });
      toast.success(`Marked ${sh.awb || sh.id} delivered`);
      loadPipeline({ background: true });
    } catch (err) {
      toast.error(err?.message || 'Failed to mark delivered');
    }
  };

  const singleScheduleSinglePickup = (sh) => {
    setSelectedIds(new Set([sh.id]));
    setPickupOpen(true);
  };

  // iter-298L — Admin Cancel AWB from the row overflow menu. Calls the
  // same endpoint the vendor uses so the flow is identical: Delhivery
  // cancel + local awb_cancelled stamp + AWB → AWB_CANCELLED state.
  const singleCancelAwb = async (sh, ord) => {
    if (!ord?.id) {
      toast.error('Order id missing for this shipment.');
      return;
    }
    const ok = window.confirm(
      `Cancel AWB ${sh.awb || sh.id}? The shipment will be marked cancelled at Delhivery and in Aniyor. The order itself will remain active — you can regenerate an AWB afterwards.`
    );
    if (!ok) return;
    try {
      await apiCall(`/api/delhivery/shipments/${ord.id}/cancel`, { method: 'POST' });
      toast.success(`AWB ${sh.awb || sh.id} cancelled`);
      loadPipeline({ background: true });
    } catch (err) {
      toast.error(err?.message || 'Failed to cancel AWB');
    }
  };

  const loadPipeline = async ({ background = false } = {}) => {
    try {
      background ? setRefreshing(true) : setLoading(true);
      const data = await apiCall('/api/shipping/vendor/pipeline-summary');
      setPipeline(data || { counts: {}, totals: {} });
    } catch (err) {
      toast.error(err?.message || 'Failed to load shipping pipeline');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    loadPipeline();
    // Refresh every 60s so the admin always sees latest carrier scans
    const t = setInterval(() => loadPipeline({ background: true }), 60000);
    return () => clearInterval(t);
  }, []);

  // Build a unique vendor list across all buckets for the vendor filter
  const vendorOptions = useMemo(() => {
    const seen = new Map();
    BUCKETS.forEach(({ id }) => {
      (pipeline[id] || []).forEach((sh) => {
        const vid = sh.vendor_id || (sh._order || {}).vendor_id;
        const vname =
          sh.vendor_name ||
          (sh._order || {}).vendor_name ||
          sh.vendor?.name ||
          'Unknown vendor';
        if (vid && !seen.has(vid)) seen.set(vid, vname);
      });
    });
    return Array.from(seen.entries())
      .map(([id, name]) => ({ id, name }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [pipeline]);

  const filterRows = (rows) => {
    const q = search.trim().toLowerCase();
    return (rows || []).filter((sh) => {
      const ord = sh._order || {};
      if (methodFilter !== 'all') {
        const m = sh.shipping_method || sh.provider || 'aniyor_managed';
        if (methodFilter === 'self' && m !== 'self_managed') return false;
        if (methodFilter === 'aniyor' && m === 'self_managed') return false;
      }
      if (vendorFilter !== 'all') {
        const vid = sh.vendor_id || ord.vendor_id;
        if (vid !== vendorFilter) return false;
      }
      if (!q) return true;
      const hay = [
        sh.awb,
        sh.awb_number,
        sh.courier_name,
        sh.vendor_name,
        ord.vendor_name,
        ord.order_number,
        ord.shopify_order_number,
        ord.customer?.name,
        ord.customer?.phone,
        ord.shipping_address?.city,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return hay.includes(q);
    });
  };

  const totalAcrossBuckets = useMemo(
    () => BUCKETS.reduce((s, b) => s + (pipeline.counts?.[b.id] || (pipeline[b.id] || []).length || 0), 0),
    [pipeline]
  );

  return (
    <DashboardLayout>
      <div className="space-y-4">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <Truck className="h-6 w-6 text-blue-600" />
              Shipping Pipeline
            </h1>
            <p className="text-sm text-gray-500">
              All deliveries across every vendor — Aniyor-managed and self-managed.
              {' '}<span className="text-gray-700 font-medium">{totalAcrossBuckets}</span> active across buckets.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => loadPipeline({ background: true })}
              data-testid="refresh-pipeline"
            >
              {refreshing ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-1" />}
              Refresh
            </Button>
          </div>
        </div>

        {/* Filters */}
        <Card>
          <CardContent className="p-3 md:p-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div className="relative">
                <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <Input
                  placeholder="Search AWB, vendor, customer, city..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-9"
                  data-testid="shipping-search"
                />
              </div>
              <Select value={methodFilter} onValueChange={setMethodFilter}>
                <SelectTrigger data-testid="method-filter">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All shipping methods</SelectItem>
                  <SelectItem value="aniyor">Aniyor-managed (Delhivery)</SelectItem>
                  <SelectItem value="self">Self-managed (vendor courier)</SelectItem>
                </SelectContent>
              </Select>
              <Select value={vendorFilter} onValueChange={setVendorFilter}>
                <SelectTrigger data-testid="vendor-filter">
                  <SelectValue placeholder="All vendors" />
                </SelectTrigger>
                <SelectContent className="max-h-72">
                  <SelectItem value="all">All vendors</SelectItem>
                  {vendorOptions.map((v) => (
                    <SelectItem key={v.id} value={v.id}>{v.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <div className="overflow-x-auto -mx-2 px-2">
            <TabsList className="inline-flex w-auto min-w-max gap-0">
              {BUCKETS.map(({ id, label, icon: Icon, color }) => {
                const count = pipeline.counts?.[id] ?? (pipeline[id] || []).length ?? 0;
                return (
                  <TabsTrigger
                    key={id}
                    value={id}
                    data-testid={`admin-tab-${id}`}
                    className="flex items-center gap-1 md:gap-2 text-xs md:text-sm"
                  >
                    <Icon className="h-3.5 w-3.5 md:h-4 md:w-4" />
                    <span>{label}</span>
                    <span className={`ml-1 px-1.5 rounded text-[10px] ${color}`}>{count}</span>
                  </TabsTrigger>
                );
              })}
            </TabsList>
          </div>

          {BUCKETS.map(({ id }) => (
            <TabsContent key={id} value={id} data-testid={`admin-${id}-content`}>
              <ShipmentTable
                rows={filterRows(pipeline[id])}
                loading={loading}
                onOpenOrder={(orderId) => orderId && navigate(`/orders/${orderId}`)}
                onMarkDelivered={singleMarkDelivered}
                onSchedulePickup={singleScheduleSinglePickup}
                onCancelAwb={singleCancelAwb}
                stage={id}
                selectedIds={selectedIds}
                onToggleSelect={toggleSelect}
                onSelectAll={selectAll}
              />
            </TabsContent>
          ))}
        </Tabs>

        {/* iter-229e — Floating bulk-action bar. Renders only when at
            least one row is selected. Actions are tab-aware:
              • NDR tab → Re-attempt (calls Delhivery)
              • Any tab with disputes or self-managed in-transit → Override → Mark Delivered
              • All tabs → Clear selection
         */}
        {selectedIds.size > 0 && (
          <BulkActionBar
            count={selectedIds.size}
            tab={activeTab}
            busy={bulkRunning}
            onClear={() => setSelectedIds(new Set())}
            onReattempt={runBulkNdrAction}
            onOverride={() => setOverrideOpen(true)}
            onSchedulePickup={() => setPickupOpen(true)}
          />
        )}

        {/* Override reason dialog */}
        <Dialog open={overrideOpen} onOpenChange={(o) => !bulkRunning && setOverrideOpen(o)}>
          <DialogContent data-testid="bulk-override-dialog">
            <DialogHeader>
              <DialogTitle>Mark {selectedIds.size} shipment{selectedIds.size === 1 ? '' : 's'} delivered</DialogTitle>
              <DialogDescription>
                The shared reason is written to every selected shipment's audit trail.
                Minimum 20 characters. This will release escrow on each shipment.
              </DialogDescription>
            </DialogHeader>
            <Textarea
              value={overrideReason}
              onChange={(e) => setOverrideReason(e.target.value)}
              placeholder="e.g. Bulk reconciliation after 18 Feb holiday — customer confirmed receipt via WhatsApp; tickets attached."
              rows={4}
              data-testid="bulk-override-reason"
            />
            <div className="text-xs text-gray-500">
              {overrideReason.trim().length} / 20 characters
            </div>
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => setOverrideOpen(false)}
                disabled={bulkRunning}
              >Cancel</Button>
              <Button
                onClick={runBulkOverride}
                disabled={bulkRunning || overrideReason.trim().length < 20}
                data-testid="confirm-bulk-override"
              >
                {bulkRunning ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <ShieldCheck className="h-4 w-4 mr-1" />}
                Confirm & deliver
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* iter-229f — Bulk Schedule Pickup dialog */}
        <Dialog open={pickupOpen} onOpenChange={(o) => !bulkRunning && setPickupOpen(o)}>
          <DialogContent data-testid="bulk-schedule-pickup-dialog">
            <DialogHeader>
              <DialogTitle>Schedule pickup for {selectedIds.size} shipment{selectedIds.size === 1 ? '' : 's'}</DialogTitle>
              <DialogDescription>
                Shipments are grouped by vendor — one pickup request goes to Delhivery per vendor.
                Self-managed shipments are excluded automatically.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Pickup date</Label>
                  <Input
                    type="date"
                    value={pickupDate}
                    onChange={(e) => setPickupDate(e.target.value)}
                    min={new Date().toISOString().slice(0, 10)}
                    data-testid="bulk-pickup-date"
                  />
                </div>
                <div>
                  <Label className="text-xs">Pickup time</Label>
                  <Input
                    type="time"
                    value={pickupTime}
                    onChange={(e) => setPickupTime(e.target.value)}
                    data-testid="bulk-pickup-time"
                  />
                </div>
              </div>
              <div>
                <Label className="text-xs">
                  Delivery co-ordinator contact no.
                  <span className="ml-1 font-normal text-gray-500">(optional override — leave blank to use each vendor's profile number)</span>
                </Label>
                <Input
                  type="tel"
                  inputMode="numeric"
                  maxLength={15}
                  placeholder="Default: each vendor's profile mobile"
                  value={pickupPhoneOverride}
                  onChange={(e) => setPickupPhoneOverride(e.target.value.replace(/[^\d+]/g, ''))}
                  data-testid="bulk-pickup-phone"
                  className="font-mono"
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setPickupOpen(false)} disabled={bulkRunning}>
                Cancel
              </Button>
              <Button
                onClick={runBulkSchedulePickup}
                disabled={bulkRunning || !pickupDate || !pickupTime}
                data-testid="confirm-bulk-schedule-pickup"
              >
                {bulkRunning ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <CalendarClock className="h-4 w-4 mr-1" />}
                Submit pickup requests
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
};

// ─── Table renderer ─────────────────────────────────────────────────────

const ShipmentTable = ({
  rows, loading, onOpenOrder, onMarkDelivered, onSchedulePickup, onCancelAwb, stage,
  selectedIds, onToggleSelect, onSelectAll,
}) => {
  if (loading) {
    return (
      <Card><CardContent className="p-8 text-center text-gray-500">
        <Loader2 className="h-6 w-6 animate-spin inline mr-2" /> Loading shipments...
      </CardContent></Card>
    );
  }
  if (!rows?.length) {
    return (
      <Card><CardContent className="p-8 text-center text-gray-500">
        <Package className="h-8 w-8 mx-auto text-gray-300 mb-2" />
        No shipments in this bucket.
      </CardContent></Card>
    );
  }
  const selectableRows = rows.filter((r) => r.id);
  const allSelected = selectableRows.length > 0 && selectableRows.every((r) => selectedIds?.has(r.id));
  const someSelected = !allSelected && selectableRows.some((r) => selectedIds?.has(r.id));
  return (
    <Card>
      <CardContent className="p-0 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 border-b">
            <tr className="text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
              <th className="px-3 py-2 w-10">
                <Checkbox
                  checked={allSelected}
                  // shadcn Checkbox supports `data-state="indeterminate"`
                  // via the `checked` prop accepting 'indeterminate'.
                  data-state={someSelected ? 'indeterminate' : allSelected ? 'checked' : 'unchecked'}
                  onCheckedChange={() => onSelectAll?.(selectableRows)}
                  data-testid="select-all-checkbox"
                  aria-label="Select all shipments in this tab"
                />
              </th>
              <th className="px-3 py-2">Order</th>
              <th className="px-3 py-2">Vendor</th>
              <th className="px-3 py-2">Customer</th>
              <th className="px-3 py-2">AWB / Courier</th>
              <th className="px-3 py-2">Method</th>
              <th className="px-3 py-2">State</th>
              <th className="px-3 py-2">Last update</th>
              <th className="px-3 py-2">Flags</th>
              <th className="px-3 py-2 text-right">Open</th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {rows.map((sh) => {
              const ord = sh._order || {};
              const isSelf = sh.shipping_method === 'self_managed' || sh.provider === 'self_managed';
              // D-075 / iter-252-AD — Prefer the SSOT resolver stamp
              // (`shipment_state` + `shipment_state_label`) over raw
              // `canonical_state` / `status`. The backend stamps these
              // via `stamp_shipment_state` in
              // `/api/shipping/vendor/pipeline-summary` so every UI row
              // reads the same truth that Order Details / Pack & Ship
              // see. Falling back to raw fields keeps backwards
              // compatibility if the endpoint is ever called without
              // the stamp.
              const ssotState = (sh.shipment_state || '').toString();
              const canon = (ssotState || sh.canonical_state || sh.status || '').toLowerCase();
              const checked = !!(sh.id && selectedIds?.has(sh.id));
              return (
                <tr
                  key={sh.id || sh.awb || ord.id}
                  className={`hover:bg-gray-50 ${checked ? 'bg-blue-50/40' : ''}`}
                  data-testid={`shipping-row-${sh.id}`}
                >
                  <td className="px-3 py-2">
                    {sh.id && (
                      <Checkbox
                        checked={checked}
                        onCheckedChange={() => onToggleSelect?.(sh.id)}
                        data-testid={`row-checkbox-${sh.id}`}
                        aria-label={`Select shipment ${sh.awb || sh.id}`}
                      />
                    )}
                  </td>
                  <td className="px-3 py-2 font-mono text-xs">
                    {ord.shopify_order_number || ord.order_number || ord.id?.slice(0, 8) || '—'}
                  </td>
                  <td className="px-3 py-2">
                    {sh.vendor_name || ord.vendor_name || (
                      <span className="text-gray-400 italic">unknown</span>
                    )}
                  </td>
                  <td className="px-3 py-2">
                    <div className="flex flex-col">
                      <span>{ord.customer?.name || '—'}</span>
                      <span className="text-xs text-gray-500 flex items-center gap-1">
                        <MapPin className="h-3 w-3" />
                        {ord.shipping_address?.city || ord.customer?.phone || '—'}
                      </span>
                    </div>
                  </td>
                  <td className="px-3 py-2">
                    <div className="flex flex-col">
                      <span className="font-mono text-xs">{sh.awb || sh.awb_number || '—'}</span>
                      <span className="text-xs text-gray-500">{sh.courier_name || sh.provider || 'Delhivery'}</span>
                    </div>
                  </td>
                  <td className="px-3 py-2">
                    {isSelf ? (
                      <Badge className="bg-purple-100 text-purple-800 hover:bg-purple-100 text-xs">
                        Self-managed
                      </Badge>
                    ) : (
                      <Badge className="bg-blue-100 text-blue-800 hover:bg-blue-100 text-xs">
                        Aniyor
                      </Badge>
                    )}
                  </td>
                  <td className="px-3 py-2">
                    <Badge
                      variant="outline"
                      className="text-xs"
                      style={sh.shipment_state_color ? {
                        // Light tint for the SSOT colour family so the
                        // badge matches Order Details / Pack & Ship.
                        backgroundColor: 'transparent',
                      } : undefined}
                    >
                      {sh.shipment_state_label || STATE_LABELS[canon] || canon || '—'}
                    </Badge>
                  </td>
                  <td className="px-3 py-2 text-xs text-gray-500">
                    {(sh.last_event && (sh.last_event_at ? `${sh.last_event} · ` : sh.last_event)) || ''}
                    {sh.last_event_at && formatRel(sh.last_event_at)}
                    {!sh.last_event && (sh.updated_at ? formatRel(sh.updated_at) : '—')}
                  </td>
                  <td className="px-3 py-2">
                    <div className="flex flex-wrap gap-1">
                      {sh.delivery_disputed && (
                        <Badge className="bg-rose-100 text-rose-800 hover:bg-rose-100 text-xs flex items-center gap-1">
                          <AlertTriangle className="h-3 w-3" /> Dispute
                        </Badge>
                      )}
                      {sh.delivery_otp_locked_for_admin_review && (
                        <Badge className="bg-amber-100 text-amber-800 hover:bg-amber-100 text-xs flex items-center gap-1">
                          <KeyRound className="h-3 w-3" /> OTP locked
                        </Badge>
                      )}
                      {sh.delivered_via && stage === 'delivered' && (
                        <Badge className="bg-emerald-50 text-emerald-700 hover:bg-emerald-50 text-xs flex items-center gap-1">
                          <ShieldCheck className="h-3 w-3" /> {prettyChannel(sh.delivered_via)}
                        </Badge>
                      )}
                      {sh.payout_status && (
                        <Badge variant="outline" className="text-xs">
                          payout: {sh.payout_status}
                        </Badge>
                      )}
                    </div>
                  </td>
                  <td className="px-3 py-2 text-right">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button
                          size="sm"
                          variant="ghost"
                          data-testid={`row-actions-${sh.id || ord.id}`}
                        >
                          <MoreHorizontal className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="w-56">
                        <DropdownMenuItem
                          onClick={() => onOpenOrder(ord.id)}
                          data-testid={`menu-open-order-${ord.id || sh.id}`}
                        >
                          <ExternalLink className="h-4 w-4 mr-2" /> Open order page
                        </DropdownMenuItem>
                        {stage === 'pending' && (
                          <DropdownMenuItem
                            onClick={() => onOpenOrder(ord.id)}
                            data-testid={`menu-generate-awb-${ord.id || sh.id}`}
                          >
                            <FilePlus className="h-4 w-4 mr-2" /> Generate AWB (on order page)
                          </DropdownMenuItem>
                        )}
                        {(stage === 'manifest' || stage === 'forward') && sh.id && !isSelf && (
                          <DropdownMenuItem
                            onClick={() => onSchedulePickup(sh)}
                            data-testid={`menu-schedule-pickup-${sh.id}`}
                          >
                            <CalendarClock className="h-4 w-4 mr-2" /> Schedule pickup
                          </DropdownMenuItem>
                        )}
                        {/* iter-298L — Reschedule pickup once already scheduled (still before physical pickup) */}
                        {(stage === 'pickup' || sh.pickup_scheduled_at || sh.pickup_date) && sh.id && !isSelf && (
                          <DropdownMenuItem
                            onClick={() => onSchedulePickup(sh)}
                            data-testid={`menu-reschedule-pickup-${sh.id}`}
                          >
                            <CalendarClock className="h-4 w-4 mr-2" /> Reschedule pickup
                          </DropdownMenuItem>
                        )}
                        {/* iter-298L — Cancel AWB whenever a live AWB exists
                            and the parcel hasn't left the vendor yet. */}
                        {sh.awb && !sh.awb_cancelled && ['manifest', 'forward', 'pickup'].includes(stage) && (
                          <>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem
                              onClick={() => onCancelAwb && onCancelAwb(sh, ord)}
                              data-testid={`menu-cancel-awb-${sh.id}`}
                              className="text-red-600"
                            >
                              <X className="h-4 w-4 mr-2" /> Cancel AWB
                            </DropdownMenuItem>
                          </>
                        )}
                        {sh.id && ['pickup', 'shipped', 'in_transit', 'ndr', 'manifest'].includes(stage) && (
                          <>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem
                              onClick={() => onMarkDelivered(sh)}
                              data-testid={`menu-mark-delivered-${sh.id}`}
                              className="text-emerald-700"
                            >
                              <ShieldCheck className="h-4 w-4 mr-2" /> Mark delivered (override)
                            </DropdownMenuItem>
                          </>
                        )}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </CardContent>
    </Card>
  );
};

const prettyChannel = (c) => {
  const map = {
    otp_doorstep: 'OTP at door',
    whatsapp_yes: 'WhatsApp YES',
    admin_override: 'Admin override',
    sla_72h_auto: '72h SLA auto',
  };
  return map[c] || c;
};

// ─── Floating bulk-action bar ──────────────────────────────────────────

const BulkActionBar = ({ count, tab, busy, onClear, onReattempt, onOverride, onSchedulePickup }) => {
  // Tab-specific actions
  const showReattempt = tab === 'ndr';
  const showSchedulePickup = ['manifest', 'forward'].includes(tab);
  // Override is sensible on any tab where shipments aren't already
  // delivered/RTO'd. Self-managed shipments stuck in_transit / picked_up
  // / shipped / disputed are the prime target.
  const showOverride = ['pickup', 'shipped', 'in_transit', 'ndr', 'manifest'].includes(tab);
  return (
    <div
      data-testid="bulk-action-bar"
      className="fixed bottom-6 left-1/2 -translate-x-1/2 z-40 flex items-center gap-3 bg-gray-900 text-white px-4 py-3 rounded-full shadow-xl"
    >
      <span className="text-sm">
        <span className="font-semibold">{count}</span> selected
      </span>
      <div className="h-4 w-px bg-gray-600" />
      {showSchedulePickup && (
        <Button
          size="sm"
          variant="secondary"
          onClick={onSchedulePickup}
          disabled={busy}
          data-testid="bulk-schedule-pickup-btn"
          className="bg-white text-gray-900 hover:bg-gray-100"
        >
          {busy ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <CalendarClock className="h-4 w-4 mr-1" />}
          Schedule pickup
        </Button>
      )}
      {showReattempt && (
        <Button
          size="sm"
          variant="secondary"
          onClick={onReattempt}
          disabled={busy}
          data-testid="bulk-reattempt-btn"
          className="bg-white text-gray-900 hover:bg-gray-100"
        >
          {busy ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <RotateCcw className="h-4 w-4 mr-1" />}
          Re-attempt with Delhivery
        </Button>
      )}
      {showOverride && (
        <Button
          size="sm"
          variant="secondary"
          onClick={onOverride}
          disabled={busy}
          data-testid="bulk-override-btn"
          className="bg-white text-gray-900 hover:bg-gray-100"
        >
          {busy ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <ShieldCheck className="h-4 w-4 mr-1" />}
          Mark delivered (override)
        </Button>
      )}
      <Button
        size="sm"
        variant="ghost"
        onClick={onClear}
        disabled={busy}
        data-testid="bulk-clear-btn"
        className="text-gray-300 hover:text-white hover:bg-gray-800"
      >
        <X className="h-4 w-4" />
      </Button>
    </div>
  );
};

const formatRel = (iso) => {
  try {
    const d = typeof iso === 'string' ? new Date(iso) : iso;
    const diffMs = Date.now() - d.getTime();
    const min = Math.floor(diffMs / 60000);
    if (min < 1) return 'just now';
    if (min < 60) return `${min}m ago`;
    const hrs = Math.floor(min / 60);
    if (hrs < 24) return `${hrs}h ago`;
    const days = Math.floor(hrs / 24);
    return `${days}d ago`;
  } catch {
    return '—';
  }
};

export default Shipping;
